import React, { useCallback, useEffect, useRef, useState } from 'react';

import { TLanguage } from 'config/tenancy';
import { IBudget } from 'modules/checkout/domain/entities';
import { CheckoutFacialRecognitionProcess } from 'modules/checkout/infra/adapters';
import { isCustomerRegistrationIncomplete } from 'modules/checkout/presentation/helpers';
import { useConfirmedGuardianDocument } from 'modules/checkout/presentation/store';
import { ICustomer } from 'shared/domain/entities';
import { makeStorageProvider } from 'shared/infra/providers';
import {
  TRoutePathsWithParams,
  useLocale,
  useNavigation,
} from 'shared/presentation/contexts';

import {
  IPreCheckoutContext,
  PreCheckoutContext,
  TBudgetSource,
  TPreCheckoutStep,
} from '../../hooks/usePreCheckout';
import { useTracking } from '../../hooks/useTracking';

const STEPS_DATA: Record<
  TPreCheckoutStep,
  {
    route: (budgetId: number) => TRoutePathsWithParams;
  }
> = {
  FACIAL_RECOGNITION: {
    route: id => `/checkout/reconhecimento-facial/${id}`,
  },
  BUDGET_SUMMARY: {
    route: id => `/checkout/resumo-orcamento/${id}`,
  },
  FINISH_REGISTRATION: {
    route: id => `/checkout/finalizar-registro/${id}`,
  },
  CART_CHECKOUT: {
    route: id => `/checkout/carrinho/${id}`,
  },
  MULTIPLE_METHODS: {
    route: id => `/checkout/multiplo/${id}`,
  },
  LINK_CHECKOUT: {
    route: id => `/checkout/link-pagamento/${id}`,
  },
};

function getFinishRegistrationStep(params: {
  customer: ICustomer;
  language: TLanguage;
  confirmedGuardianDocument: string | null;
}): TPreCheckoutStep | undefined {
  if (
    !isCustomerRegistrationIncomplete(
      params.customer,
      params.language,
      params.confirmedGuardianDocument,
    )
  ) {
    return;
  }

  return 'FINISH_REGISTRATION';
}

function getFacialRecognitionSteps(params: {
  budget: IBudget;
}): TPreCheckoutStep[] {
  if (params.budget.signingMode !== 'FACIAL') return [];

  const storage = makeStorageProvider();

  const validRecognitions = storage.get('VALID_FACIAL_RECOGNITIONS');

  const process = new CheckoutFacialRecognitionProcess({
    customerId: params.budget.customer.id,
    budgetId: params.budget.id,
  });

  const isThisBudgetAlreadyValidated = validRecognitions?.some(
    storedProcess => storedProcess.key === process.key,
  );

  if (isThisBudgetAlreadyValidated) return [];

  return ['BUDGET_SUMMARY', 'FACIAL_RECOGNITION'];
}

function getCheckoutStep(params: {
  budget: IBudget;
  source: TBudgetSource;
}): TPreCheckoutStep {
  if (params.source === 'CART') return 'CART_CHECKOUT';

  if (params.budget.simultaneousPaymentMethods > 1) {
    return 'MULTIPLE_METHODS';
  }

  return 'LINK_CHECKOUT';
}

const PreCheckoutProvider: React.FC = ({ children }) => {
  const [budgetId, setBudgetId] =
    useState<IPreCheckoutContext['budgetId']>(undefined);
  const currentStep = useRef(0);
  const steps = useRef<TPreCheckoutStep[]>([]);
  const { openLinkPayment } = useTracking();
  const { language } = useLocale('checkout');
  const navigation = useNavigation();
  const confirmedGuardianDocument = useConfirmedGuardianDocument();

  const start = useCallback<IPreCheckoutContext['start']>(
    ({ budget, source }) => {
      steps.current = [];
      currentStep.current = 0;
      setBudgetId(budget.id);
      openLinkPayment({
        budgetId: budget.id.toString(),
      });
      const finishRegistrationStep = getFinishRegistrationStep({
        customer: budget.customer,
        language,
        confirmedGuardianDocument,
      });

      if (finishRegistrationStep) {
        steps.current.push(finishRegistrationStep);
      }

      const facialRecognitionSteps = getFacialRecognitionSteps({
        budget,
      });
      steps.current.push(...facialRecognitionSteps);

      const checkoutStep = getCheckoutStep({ budget, source });
      steps.current.push(checkoutStep);

      const firstStep = STEPS_DATA[steps.current[0]];
      const route = firstStep.route(budget.id);
      navigation.push(route);
    },
    [language, navigation, confirmedGuardianDocument],
  );

  const nextStep = useCallback<IPreCheckoutContext['nextStep']>(() => {
    const nextStep = currentStep.current + 1;

    const nextStepKey = steps.current[nextStep];

    if (!nextStepKey || !budgetId) return;

    currentStep.current = nextStep;
    const nextStepData = STEPS_DATA[nextStepKey];
    const route = nextStepData.route(budgetId);
    navigation.push(route);
  }, [navigation, budgetId]);

  const hasStep = useCallback<IPreCheckoutContext['hasStep']>(
    step => steps.current.includes(step),
    [],
  );

  // In case the user uses the browser's native navigation handler to go back and forth from the checkout pages,
  // we need to synchronize the currentStep to prevent them from jumping/missing steps
  useEffect(() => {
    if (!budgetId) return;

    const popstateListener = () => {
      const newPathname = document.location.pathname;

      const newPathAssociatedRoute = steps.current.findIndex(
        step => STEPS_DATA[step].route(budgetId) === newPathname,
      );

      currentStep.current = Math.max(newPathAssociatedRoute, 0);
    };

    window.addEventListener('popstate', popstateListener);

    return () => {
      window.removeEventListener('popstate', popstateListener);
    };
  }, [budgetId]);

  return (
    <PreCheckoutContext.Provider
      value={{
        budgetId,
        activeSteps: steps.current,
        start,
        nextStep,
        hasStep,
      }}
    >
      {children}
    </PreCheckoutContext.Provider>
  );
};

export default PreCheckoutProvider;
